import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, RefreshCw, ClipboardList, Bug, CheckCircle2, XCircle,
  AlertCircle, Trash2, Download, Copy, ExternalLink, Plus, ChevronRight, ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { showFriendlyError } from '../utils/friendlyErrors';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TESTER_URL = process.env.REACT_APP_BACKEND_URL || '';

// Stripe test card info displayed prominently for the tester.
const STRIPE_TEST_CARD = {
  number: '4242 4242 4242 4242',
  exp: 'any future date (e.g. 12/30)',
  cvc: 'any 3 digits (e.g. 123)',
  zip: 'any 5 digits (e.g. 90210)',
};

export default function AdminQAConsolePage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all'); // all | not_started | in_progress | passed | failed
  const [showBugForm, setShowBugForm] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, bugsRes] = await Promise.all([
        axios.get(`${API}/admin/qa/status`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/admin/qa/bugs`, { ...getAuthHeader(), withCredentials: true }),
      ]);
      setData(statusRes.data);
      setBugs(bugsRes.data.items || []);
    } catch (err) {
      showFriendlyError(err, { context: 'QA status' });
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'super_admin', 'org_admin'].includes(user.role)) {
      toast.error('Admin access required');
      navigate('/app/dashboard');
      return;
    }
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, [user, navigate, fetchAll]);

  // Auto-refresh status every 30s so admin sees tester progress live
  useEffect(() => {
    if (loading) return;
    const t = setInterval(fetchAll, 30_000);
    return () => clearInterval(t);
  }, [loading, fetchAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  };

  const markResult = async (test_id, status) => {
    const notes = window.prompt(`Notes for ${test_id} (optional):`) || '';
    try {
      await axios.post(`${API}/admin/qa/test-cases/result`,
        { test_id, status, notes },
        { ...getAuthHeader(), withCredentials: true }
      );
      toast.success(`${test_id} marked ${status}`);
      fetchAll();
    } catch (err) {
      showFriendlyError(err, { context: 'Save result' });
    }
  };

  const handleReset = async () => {
    // First do dry-run
    let dryRun;
    try {
      const r = await axios.post(`${API}/admin/qa/reset`, {}, { ...getAuthHeader(), withCredentials: true });
      dryRun = r.data;
    } catch (err) {
      toast.error('Reset preflight failed');
      return;
    }
    const wd = dryRun.would_delete || {};
    if (wd.parents === 0 && wd.children === 0) {
      toast.info('Nothing to reset, no QA accounts currently exist.');
      return;
    }
    if (!window.confirm(
      `⚠️ This will permanently delete:\n` +
      `  • ${wd.parents} QA parent accounts\n` +
      `  • ${wd.children} child accounts\n` +
      `  • all associated family links, consents, test results\n\n` +
      `(Only emails matching the QA whitelist are affected, production data is safe.)\n\n` +
      `Are you absolutely sure?`
    )) return;

    try {
      const r = await axios.post(`${API}/admin/qa/reset?confirm=true`, {}, { ...getAuthHeader(), withCredentials: true });
      toast.success(
        `Reset complete, deleted ${r.data.deleted.parents} parents, ${r.data.deleted.children} children`
      );
      fetchAll();
    } catch (err) {
      showFriendlyError(err, { context: 'Reset QA data' });
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['Test ID', 'Parent ID', 'Parent Name', 'Email', 'Password', 'Scenario',
       'Expected Children', 'Registered', 'Paid', 'Onboarded',
       'Children Added', 'Consents Signed', 'Auto Status', 'Manual Status', 'Notes'],
      ...data.test_cases.map(tc => [
        tc.test_id, tc.parent_id, tc.parent_name, tc.parent_email, 'Test123!',
        tc.scenario, tc.expected_child_count,
        tc.registered, tc.has_paid, tc.onboarding_complete,
        tc.children_added, tc.consents_signed,
        tc.auto_status, tc.manual_status || '', (tc.notes || '').replace(/[\r\n,]/g, ' '),
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `qa-results-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1729] text-white flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  const tcases = data?.test_cases || [];
  const filtered = tcases.filter((t) => {
    if (filter === 'all') return true;
    if (filter === 'not_started') return !t.registered;
    if (filter === 'in_progress') return t.registered && t.manual_status !== 'pass' && t.manual_status !== 'fail';
    if (filter === 'passed') return t.manual_status === 'pass';
    if (filter === 'failed') return t.manual_status === 'fail';
    return true;
  });

  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-[#0a1729] text-white" data-testid="admin-qa-console-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 bg-fuchsia-600/20 rounded-lg flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-fuchsia-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">QA Console</h1>
              <p className="text-sm text-slate-300">
                Live tracker for the 20 QA test cases, tester registers via the real flow at the URL below.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline" className="border-slate-700" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={exportCSV} variant="outline" className="border-slate-700" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button onClick={handleReset} variant="outline" className="border-red-700 text-red-300 hover:bg-red-900/30" data-testid="reset-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Reset QA Data
            </Button>
          </div>
        </div>

        {/* Tester Instructions Card */}
        <div className="bg-gradient-to-br from-fuchsia-900/30 to-blue-900/20 border border-fuchsia-700/40 rounded-xl p-5 mb-6" data-testid="tester-instructions-card">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            🧪 Share this with your QA tester
          </h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-slate-300 mb-1">Preview/Test URL (no real charges)</div>
              <div className="flex gap-2 items-center bg-slate-900/60 rounded-lg px-3 py-2">
                <code className="text-emerald-300 break-all flex-1" data-testid="tester-url">{TESTER_URL}</code>
                <button onClick={() => copyToClipboard(TESTER_URL, 'URL')} className="text-slate-300 hover:text-white" title="Copy">
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <a href={TESTER_URL} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-white" title="Open">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
            <div>
              <div className="text-slate-300 mb-1">Stripe TEST card (in checkout)</div>
              <div className="bg-slate-900/60 rounded-lg px-3 py-2 space-y-1 text-xs font-mono">
                <div>Number: <span className="text-emerald-300">{STRIPE_TEST_CARD.number}</span></div>
                <div>Exp: {STRIPE_TEST_CARD.exp} · CVC: {STRIPE_TEST_CARD.cvc} · ZIP: {STRIPE_TEST_CARD.zip}</div>
              </div>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-300 bg-slate-900/40 rounded-lg p-3">
            <strong className="text-fuchsia-300">For each row below:</strong> tester opens the URL → clicks Sign Up → selects the relevant plan (Family Plan for parents with kids, Young Adult for solo) → enters the email/password shown → completes Stripe TEST checkout → finishes onboarding → invites each child via the family page → child receives email → child accepts. This page auto-refreshes every 30s.
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <StatCard label="Total" value={summary.total ?? '-'} color="slate" />
          <StatCard label="Registered" value={summary.registered ?? 0} color="blue" />
          <StatCard label="Paid" value={summary.paid ?? 0} color="cyan" />
          <StatCard label="Onboarded" value={summary.fully_onboarded ?? 0} color="purple" />
          <StatCard label="✓ Pass" value={summary.marked_pass ?? 0} color="emerald" />
          <StatCard label="✗ Fail" value={summary.marked_fail ?? 0} color="red" />
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2" data-testid="qa-filter">
          {[
            { id: 'all', label: 'All' },
            { id: 'not_started', label: 'Not Started' },
            { id: 'in_progress', label: 'In Progress' },
            { id: 'passed', label: '✓ Passed' },
            { id: 'failed', label: '✗ Failed' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                filter === f.id ? 'bg-fuchsia-600 text-white' : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Test case rows */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden mb-8" data-testid="qa-test-cases-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/60 text-slate-300 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Test</th>
                <th className="px-3 py-2 text-left">Parent</th>
                <th className="px-3 py-2 text-left">Scenario</th>
                <th className="px-3 py-2 text-center">Reg</th>
                <th className="px-3 py-2 text-center">Pay</th>
                <th className="px-3 py-2 text-center">Onboard</th>
                <th className="px-3 py-2 text-center">Kids</th>
                <th className="px-3 py-2 text-center">Consents</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Mark</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tc) => (
                <ExpandableTestRow
                  key={tc.test_id}
                  tc={tc}
                  copyToClipboard={copyToClipboard}
                  markResult={markResult}
                />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-slate-300 text-sm">No test cases match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bug Tracker */}
        <BugSection
          bugs={bugs}
          onChange={fetchAll}
          showForm={showBugForm}
          setShowForm={setShowBugForm}
          getAuthHeader={getAuthHeader}
        />
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    slate: 'bg-slate-800/60 text-slate-200',
    blue: 'bg-blue-900/30 text-blue-300',
    cyan: 'bg-cyan-900/30 text-cyan-300',
    purple: 'bg-purple-900/30 text-purple-300',
    emerald: 'bg-emerald-900/30 text-emerald-300',
    red: 'bg-red-900/30 text-red-300',
  };
  return (
    <div className={`rounded-lg p-3 ${colors[color] || colors.slate}`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    </div>
  );
}

function Check() {
  return <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />;
}
function Dash() {
  return <span className="text-slate-300">-</span>;
}

function StatusPill({ auto, manual }) {
  if (manual === 'pass') return <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white">✓ PASS</span>;
  if (manual === 'fail') return <span className="text-[10px] px-2 py-0.5 rounded bg-red-600 text-white">✗ FAIL</span>;
  const map = {
    not_started: { c: 'bg-slate-700 text-slate-300', t: 'not started' },
    registered_no_payment: { c: 'bg-amber-700/40 text-amber-300', t: 'no payment' },
    payment_done_no_onboarding: { c: 'bg-blue-700/40 text-blue-300', t: 'paid · onboarding' },
    ready_for_qa: { c: 'bg-fuchsia-700/40 text-fuchsia-300', t: 'ready for QA' },
  };
  const key = (auto || '').split(' ')[0];
  const m = map[key] || { c: 'bg-slate-700 text-slate-300', t: auto || '-' };
  return <span className={`text-[10px] px-2 py-0.5 rounded ${m.c}`}>{m.t}</span>;
}

function ExpandableTestRow({ tc, copyToClipboard, markResult }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = tc.expected_child_count > 0;
  return (
    <>
      <tr className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`row-${tc.test_id}`}>
        <td className="px-3 py-2 font-mono text-xs">
          <div className="flex items-center gap-1">
            {hasChildren ? (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="text-slate-300 hover:text-white"
                data-testid={`expand-${tc.test_id}`}
                title={expanded ? 'Hide children' : 'Show children'}
              >
                {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            ) : <span className="w-3.5 inline-block" />}
            {tc.test_id}
          </div>
        </td>
        <td className="px-3 py-2">
          <div className="font-medium text-xs">{tc.parent_name}</div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-300">
            <span className="truncate max-w-[200px]">{tc.parent_email}</span>
            <button
              onClick={() => copyToClipboard(tc.parent_email, 'Email')}
              className="text-slate-300 hover:text-white"
              title="Copy email"
              data-testid={`copy-email-${tc.test_id}`}
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
        </td>
        <td className="px-3 py-2 text-xs text-slate-300">{tc.scenario}</td>
        <td className="px-3 py-2 text-center">{tc.registered ? <Check /> : <Dash />}</td>
        <td className="px-3 py-2 text-center">{tc.has_paid ? <Check /> : <Dash />}</td>
        <td className="px-3 py-2 text-center">{tc.onboarding_complete ? <Check /> : <Dash />}</td>
        <td className="px-3 py-2 text-center text-xs">
          <span className={tc.children_added >= tc.expected_child_count ? 'text-emerald-400' : 'text-amber-400'}>
            {tc.children_added}/{tc.expected_child_count}
          </span>
        </td>
        <td className="px-3 py-2 text-center text-xs">
          <span className={tc.consents_signed >= tc.expected_consents ? 'text-emerald-400' : 'text-slate-300'}>
            {tc.consents_signed}/{tc.expected_consents}
          </span>
        </td>
        <td className="px-3 py-2 text-center">
          <StatusPill auto={tc.auto_status} manual={tc.manual_status} />
        </td>
        <td className="px-3 py-2 text-right">
          <div className="inline-flex gap-1">
            <button
              onClick={() => markResult(tc.test_id, 'pass')}
              className={`p-1.5 rounded ${tc.manual_status === 'pass' ? 'bg-emerald-600 text-white' : 'bg-slate-800 hover:bg-emerald-700/40 text-slate-300'}`}
              title="Mark Pass"
              data-testid={`mark-pass-${tc.test_id}`}
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => markResult(tc.test_id, 'fail')}
              className={`p-1.5 rounded ${tc.manual_status === 'fail' ? 'bg-red-600 text-white' : 'bg-slate-800 hover:bg-red-700/40 text-slate-300'}`}
              title="Mark Fail"
              data-testid={`mark-fail-${tc.test_id}`}
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      {expanded && hasChildren && (
        <tr className="bg-slate-900/80" data-testid={`children-${tc.test_id}`}>
          <td colSpan={10} className="px-4 py-3">
            <div className="text-[11px] text-slate-300 mb-2">
              <strong className="text-fuchsia-300">Children to invite</strong>, use these exact emails / names when {tc.parent_name} invites each child:
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {(tc.expected_children || []).map((c) => (
                <div
                  key={c.child_id}
                  className="bg-slate-800/40 border border-slate-700 rounded-lg p-2.5 text-xs"
                  data-testid={`child-${tc.test_id}-${c.child_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[10px] text-slate-300">{c.child_id}</span>
                    <span className="text-[10px] text-slate-300">{c.grade} · age {c.age}</span>
                  </div>
                  <div className="font-medium text-slate-200">{c.name}</div>
                  <div className="flex items-center gap-1 mt-1 text-[11px] text-emerald-300 break-all">
                    <span className="font-mono">{c.email}</span>
                    <button
                      onClick={() => copyToClipboard(c.email, 'Child email')}
                      className="text-slate-300 hover:text-white shrink-0"
                      title="Copy"
                      data-testid={`copy-child-email-${c.child_id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="text-[10px] text-slate-300 mt-0.5">
                    password: <span className="font-mono text-slate-300">Test123!</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 text-[10px] text-slate-300 bg-[#0a1729]/50 rounded p-2">
              💡 <strong>Tip, receive all 50 invite emails in ONE inbox:</strong> set up a catch-all alias on <code>@futurepathai.io</code>{' '}
              that forwards to your tester&apos;s mailbox, or override Resend&apos;s TO address with Gmail plus-tags like <code>tester+child001@gmail.com</code>.
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function BugSection({ bugs, onChange, showForm, setShowForm, getAuthHeader }) {
  const [form, setForm] = useState({
    module: '', priority: 'High', description: '',
    expected_result: '', actual_result: '', related_test_id: '', screenshot_url: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.module || !form.description) {
      toast.error('Module and description are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/qa/bugs`, form, { ...getAuthHeader(), withCredentials: true });
      toast.success('Bug logged');
      setForm({ module: '', priority: 'High', description: '', expected_result: '', actual_result: '', related_test_id: '', screenshot_url: '' });
      setShowForm(false);
      onChange();
    } catch (err) {
      showFriendlyError(err, { context: 'Log bug' });
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (bug_id, status) => {
    try {
      await axios.put(`${API}/admin/qa/bugs/status`, { bug_id, status }, { ...getAuthHeader(), withCredentials: true });
      toast.success(`${bug_id} → ${status}`);
      onChange();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="bug-tracker-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Bug className="w-5 h-5 text-red-400" /> Bug Tracker
          <span className="text-xs text-slate-300 font-normal">({bugs.length})</span>
        </h2>
        <Button
          onClick={() => setShowForm((v) => !v)}
          variant="outline"
          className="border-slate-700"
          data-testid="toggle-bug-form-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> {showForm ? 'Cancel' : 'Log Bug'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-slate-800/40 rounded-lg p-4 mb-4 space-y-3" data-testid="bug-form">
          <div className="grid md:grid-cols-3 gap-3">
            <Input
              placeholder="Module (e.g. Registration)"
              value={form.module}
              onChange={(e) => setForm({ ...form, module: e.target.value })}
              data-testid="bug-module"
            />
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm"
              data-testid="bug-priority"
            >
              <option>Critical</option><option>High</option><option>Medium</option><option>Low</option>
            </select>
            <Input
              placeholder="Related Test ID (e.g. T003)"
              value={form.related_test_id}
              onChange={(e) => setForm({ ...form, related_test_id: e.target.value })}
              data-testid="bug-related-test"
            />
          </div>
          <textarea
            placeholder="Description, what went wrong?"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-sm h-20"
            data-testid="bug-description"
          />
          <div className="grid md:grid-cols-2 gap-3">
            <Input
              placeholder="Expected result"
              value={form.expected_result}
              onChange={(e) => setForm({ ...form, expected_result: e.target.value })}
              data-testid="bug-expected"
            />
            <Input
              placeholder="Actual result"
              value={form.actual_result}
              onChange={(e) => setForm({ ...form, actual_result: e.target.value })}
              data-testid="bug-actual"
            />
          </div>
          <Input
            placeholder="Screenshot URL (optional)"
            value={form.screenshot_url}
            onChange={(e) => setForm({ ...form, screenshot_url: e.target.value })}
            data-testid="bug-screenshot"
          />
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700"
            data-testid="submit-bug-btn"
          >
            {submitting ? 'Saving…' : 'Log Bug'}
          </Button>
        </div>
      )}

      {bugs.length === 0 ? (
        <div className="text-slate-300 text-sm py-6 text-center">No bugs logged yet.</div>
      ) : (
        <div className="space-y-2" data-testid="bug-list">
          {bugs.map((b) => (
            <div key={b.bug_id} className="bg-slate-800/30 rounded-lg p-3 text-sm flex items-start gap-3" data-testid={`bug-${b.bug_id}`}>
              <div className="flex-shrink-0">
                <span className={`text-[10px] px-2 py-0.5 rounded ${
                  b.priority === 'Critical' ? 'bg-red-700 text-white' :
                  b.priority === 'High' ? 'bg-red-600/40 text-red-300' :
                  b.priority === 'Medium' ? 'bg-amber-600/40 text-amber-300' :
                  'bg-slate-700 text-slate-300'
                }`}>{b.priority}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs text-slate-300">{b.bug_id}</code>
                  <span className="text-xs text-slate-300">·</span>
                  <span className="text-xs text-slate-300">{b.module}</span>
                  {b.related_test_id && (
                    <>
                      <span className="text-xs text-slate-300">·</span>
                      <span className="text-xs text-fuchsia-300">{b.related_test_id}</span>
                    </>
                  )}
                </div>
                <div className="text-slate-200 mt-1">{b.description}</div>
                {(b.expected_result || b.actual_result) && (
                  <div className="text-xs text-slate-300 mt-1">
                    {b.expected_result && <div><strong>Expected:</strong> {b.expected_result}</div>}
                    {b.actual_result && <div><strong>Actual:</strong> {b.actual_result}</div>}
                  </div>
                )}
                <div className="text-[10px] text-slate-300 mt-1">
                  {new Date(b.date).toLocaleString()} · by {b.reported_by}
                </div>
              </div>
              <select
                value={b.status}
                onChange={(e) => updateStatus(b.bug_id, e.target.value)}
                className={`text-xs rounded px-2 py-1 border-0 ${
                  b.status === 'Fixed' || b.status === 'Closed' ? 'bg-emerald-700/40 text-emerald-300' :
                  b.status === 'In Progress' ? 'bg-blue-700/40 text-blue-300' :
                  b.status === 'Wont Fix' ? 'bg-slate-700 text-slate-300' :
                  'bg-red-700/40 text-red-300'
                }`}
                data-testid={`bug-status-${b.bug_id}`}
              >
                <option>Open</option>
                <option>In Progress</option>
                <option>Fixed</option>
                <option>Closed</option>
                <option>Wont Fix</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
